export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type ApiEnvelope<T> = {
  data: T;
  meta?: Record<string, unknown>;
};

function errorFromPayload(payload: unknown): { code: string; message: string } {
  if (!payload || typeof payload !== "object") {
    return { code: "REQUEST_FAILED", message: "请求失败，请稍后重试。" };
  }
  const error = (payload as { error?: unknown }).error;
  if (!error || typeof error !== "object") {
    return { code: "REQUEST_FAILED", message: "请求失败，请稍后重试。" };
  }
  const candidate = error as { code?: unknown; message?: unknown };
  return {
    code: typeof candidate.code === "string" ? candidate.code : "REQUEST_FAILED",
    message: typeof candidate.message === "string" ? candidate.message : "请求失败，请稍后重试。",
  };
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers,
  });
  const payload: unknown = response.status === 204
    ? null
    : await response.json().catch(() => null);

  if (!response.ok) {
    const error = errorFromPayload(payload);
    window.dispatchEvent(new CustomEvent("creator-radar:api-error", {
      detail: { status: response.status, code: error.code },
    }));
    throw new ApiError(response.status, error.code, error.message);
  }

  if (response.status === 204) return null as T;
  return (payload as ApiEnvelope<T>).data;
}

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(value);
}
